/**
 * Single source of truth for store-level commercial rules.
 *
 * These values were previously hardcoded in several places (checkout, cart,
 * admin order details, invoice), which meant changing a shipping rate required
 * editing multiple files and the copies drifted apart. Until there is an admin
 * Settings screen backed by the API, this module is the one place to edit them.
 */

/** State the business is registered in — decides CGST+SGST vs IGST on an order. */
export const BUSINESS_STATE = 'Odisha';

/**
 * Seller identity printed on the invoice header.
 *
 * Fallback only. `GET /api/settings` returns a `business` object that the admin
 * edits under Settings → Business / Invoice, and that always wins — these values
 * exist for the first paint and for a failed settings fetch. A stale GSTIN on a
 * tax invoice is not a cosmetic problem, so nothing here may be preferred over
 * what the admin saved.
 */
export const BUSINESS_DETAILS = {
  name: 'NSO SERVICES PRIVATE LIMITED',
  address: 'NEAR NEW COURT, GF-1 Sumitra Heights, KACHERY ROAD UDITNAGAR, ROURKELA, SUNDERGARH',
  email: 'nsooptical@gmail.com',
  phone: '9938831038',
  gstin: '21AAGCN8284N1ZY'
};

/** Flat shipping charge applied below the free-shipping threshold. */
export const SHIPPING_FLAT = 99;

/** Order value at or above which shipping is free. */
export const FREE_SHIPPING_THRESHOLD = 999;

/**
 * Last-resort GST rate (%) for a product that carries no tax fields at all.
 *
 * This is NOT a default for a product configured at 0% — 0 is a real rate (an
 * exempt item) and must survive every code path untouched. Admin Settings
 * overrides this via `default_gst_rate`.
 */
export const DEFAULT_GST_RATE = 18;

/**
 * True when the delivery state matches the business state (intra-state supply).
 *
 * `homeState` is passed in by callers that have the admin's configured state from
 * settings; it falls back to the constant only when they don't.
 */
export const isIntraState = (state, homeState = BUSINESS_STATE) =>
  !!state && !!homeState && state.trim().toLowerCase() === String(homeState).trim().toLowerCase();

/**
 * Reads a percentage off an API payload.
 *
 * Returns null — never a fallback — for "not configured", so callers can tell a
 * deliberate 0% apart from a missing field. `Number('')` is 0, which is why the
 * empty string has to be rejected before the cast.
 */
export const parseRate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
};

/**
 * Total GST rate (%) configured on a product or order item.
 *
 * A product carries cgst/sgst (intra-state) and igst (inter-state); some order
 * payloads carry a single `gst_rate` instead. The destination state decides how
 * the tax is *labelled*, not how much it is, so this returns one total rate and
 * `gstTotals` splits it — an address change must never move the grand total.
 *
 * Returns null when the payload carries no tax fields at all, so the caller
 * chooses the fallback. It returns 0 — not null — for a product explicitly set
 * to 0/0/0, which is the whole point of this function.
 */
export const gstRateFor = (source) => {
  const cgst = parseRate(source?.cgst);
  const sgst = parseRate(source?.sgst);
  const igst = parseRate(source?.igst);
  const flat = parseRate(source?.gst_rate);

  // null only when neither half of the pair was sent; 0 when they were sent as 0.
  const intra = cgst === null && sgst === null ? null : (cgst ?? 0) + (sgst ?? 0);

  // Prefer whichever side is actually filled in. A store that configures only
  // IGST must not read as 0% GST, and one that configures only CGST+SGST must
  // not either — both are common, and only one of them is ever populated.
  if (intra) return intra;
  if (igst) return igst;
  if (flat) return flat;

  // Everything present is 0 → the product is exempt. Say so.
  if (intra !== null || igst !== null || flat !== null) return 0;

  return null; // nothing configured
};

/**
 * Splits a GST-inclusive amount into its taxable value and tax component.
 *
 * The rate defaults to 0, not to DEFAULT_GST_RATE: a caller that forgets to
 * resolve a rate should under-report tax visibly rather than silently invent
 * 18% on an exempt product.
 *
 * @param {number} inclusiveAmount amount that already contains the tax
 * @param {number} ratePercent GST rate, e.g. 18
 */
export const splitInclusiveTax = (inclusiveAmount, ratePercent = 0) => {
  const amount = Number(inclusiveAmount) || 0;
  const rate = parseRate(ratePercent) ?? 0;
  const taxable = amount / (1 + rate / 100);
  return { taxable, tax: amount - taxable };
};

/**
 * Aggregates GST across lines whose prices already include the tax.
 *
 * Every screen that shows a tax figure (cart, checkout, admin order, invoice)
 * runs through here, so they cannot disagree with each other.
 *
 * @param {Array<{amount:number, rates:object}>} lines line total (incl. tax) +
 *        the product/order-item carrying cgst/sgst/igst
 * @param {object}  opts
 * @param {boolean} opts.intraState CGST+SGST when true, IGST when false
 * @param {number|null} opts.defaultRate rate for lines with no tax fields at all
 */
export const gstTotals = (lines, { intraState = true, defaultRate = null } = {}) => {
  const fallback = parseRate(defaultRate) ?? 0;

  return (lines || []).reduce(
    (acc, line) => {
      const amount = Number(line?.amount) || 0;
      const rate = gstRateFor(line?.rates) ?? fallback;
      const { taxable, tax } = splitInclusiveTax(amount, rate);

      let cgst = 0;
      let sgst = 0;
      let igst = 0;

      if (intraState) {
        // Split by the line's own CGST/SGST when both are configured; half/half
        // is only a display convention for the common symmetric case.
        const c = parseRate(line?.rates?.cgst);
        const s = parseRate(line?.rates?.sgst);
        const pair = (c ?? 0) + (s ?? 0);
        if (c !== null && s !== null && pair > 0) {
          cgst = tax * (c / pair);
          sgst = tax * (s / pair);
        } else {
          cgst = tax / 2;
          sgst = tax / 2;
        }
      } else {
        igst = tax;
      }

      return {
        taxable: acc.taxable + taxable,
        tax: acc.tax + tax,
        cgst: acc.cgst + cgst,
        sgst: acc.sgst + sgst,
        igst: acc.igst + igst,
      };
    },
    { taxable: 0, tax: 0, cgst: 0, sgst: 0, igst: 0 }
  );
};
